import {Injectable} from '@angular/core';
import {Observable} from 'rxjs/Observable';
import {User} from '../../models/user';
import {Agency} from '../../models/agency';
import {NavigationExtras, Router} from '@angular/router';
import {environment} from '../../../environments/environment';
import {AccessToken} from '../../models/access-token';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {AccessTokenService} from '../access-token/access-token.service';
import {CookieService} from '../access-token/cookie.service';

interface TokenResponse {
  token: string;
}

/**
 * Handles user authentication.
 */
@Injectable()
export class AuthenticationService {
  user = new BehaviorSubject<User | undefined>(undefined);
  isLoggedIn = new BehaviorSubject<boolean>(false);

  constructor(private http: HttpClient,
              private router: Router,
              private tokenService: AccessTokenService<AccessToken>,
              private cookieService: CookieService) {
    this.setupIsLoggedInMapping();
    this.setupUserMapping();
    this.setupTokenRefresh();
    this.setupLogoutWhenTokenExpires();
  }

  login(username: string, password: string, agencyISIL: string) {
    return this.http
      .post<TokenResponse>(`${environment.apiUrl}/auth/login`, {username, password, agencyISIL})
      .do(response => this.tokenService.setToken(response.token));
  }

  logout(redirectBack = false) {
    const user = this.user.getValue();
    const params: NavigationExtras = {};

    if (!user) {
      return Observable.empty();
    }

    if (redirectBack) {
      params.queryParams = {redirectTo: this.router.routerState.snapshot.url};
    }

    return this.http
      .delete(`${environment.apiUrl}/auth/logout`, {headers: this.getAuthHeaders(), responseType: 'text'})
      .do(() => this.tokenService.removeToken())
      .do(() => this.router.navigate([`/agency/${user.agency.ISIL}/login`], params));
  }

  /**
   * Gets an agency given an ISIL id.
   * Returns undefined if the agency hasn't bought access to Cicero Move.
   * @param ISIL
   * @returns {Observable<Agency>}
   */
  getAgency(ISIL: string): Observable<Agency> {
    return this.http.get<{ ISIL: string, name: string }>(`${environment.apiUrl}/auth/agency/${ISIL}`)
      .map(agency => new Agency(agency.ISIL, agency.name))
      .catch(error => Observable.of(undefined));
  }

  getAuthHeaders(): HttpHeaders {
    return new HttpHeaders().set('Authorization', `Bearer ${this.cookieService.getCookie('token')}`);
  }

  /**
   * Maps the current token to the current login status
   */
  private setupIsLoggedInMapping() {
    this.tokenService.decodedToken
      .map(token => !!token)
      .subscribe(this.isLoggedIn);
  }

  /**
   * Maps the token payload to a User object
   */
  private setupUserMapping() {
    this.tokenService.decodedToken
      .map(token => token ? new User(token.username, new Agency(token.agencyISIL, token.agencyName)) : undefined)
      .subscribe(this.user);
  }

  /**
   * Will keep fetching a refreshed access token shortly before the current access token expires.
   */
  private setupTokenRefresh() {
    if (environment.disableLongRunningTasks) {
      return;
    }

    const getToken = this.http
      .get<TokenResponse>(`${environment.apiUrl}/auth/refresh-access`)
      .map(response => response.token);

    this.tokenService.setupTokenRefresh(getToken);
  }

  /**
   * Will redirect the user to the login page if the current token expires.
   * If setupTokenRefresh has been called, this will only happen when the refresh token has expired
   * and therefore a refreshed access token can't be fetched.
   */
  private setupLogoutWhenTokenExpires() {
    if (environment.disableLongRunningTasks) {
      return;
    }

    this.tokenService.expired
      .switchMap(() => this.logout(true))
      .subscribe();
  }
}



// WEBPACK FOOTER //
// ./src/app/authentication/shared/authentication.service.ts